import React from "react";
import ReactDom from "react-dom";
import { AnimatePresence } from "framer-motion";
import { get } from "../../constants/theme";
import Fade from "../Animations/Fade";
import Box from "../Box/Box";
import MotionBox from "../Box/MotionBox";

type Props = {
  isOpen?: boolean;
  onClose: () => void;
  backgroundColor?: string;
  color?: string;
  closeColor?: string;
  zIndex?: number;
  children: React.ReactNode;
};

const Popup = ({
  isOpen = true,
  onClose,
  backgroundColor,
  color,
  closeColor,
  zIndex = 300,
  children,
}: Props) => {
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (typeof document === "undefined") return null;

  const portalRoot = document.getElementById("portal");
  if (!portalRoot) return null;

  const crossBg = closeColor ?? color ?? get("palette.text.dark_gold");

  return ReactDom.createPortal(
    <AnimatePresence>
      {isOpen && (
        <Fade>
          <Box
            sx={{
              zIndex,
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              // @ts-ignore — dvh для корректной высоты на мобильных
              "@supports (height: 100dvh)": {
                height: "100dvh",
              },
              backgroundColor:
                backgroundColor ?? get("palette.background.gold"),
              color: color ?? get("palette.text.dark_gold"),
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                minHeight: "100vh",
                // @ts-ignore — dvh для корректной высоты на мобильных
                "@supports (min-height: 100dvh)": {
                  minHeight: "100dvh",
                },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                paddingTop: "80px",
                paddingBottom: "80px",
              }}
            >
              {children}
            </Box>

            <MotionBox
              sx={{
                cursor: "pointer",
                position: "absolute",
                left: ["16px", "5%"],
                top: ["45px", "50px"],
                transform: "translateY(-50%)",
                zIndex: 101,
              }}
              onClick={onClose}
            >
              <Box
                sx={{
                  height: [40, 40, 46, 46],
                  width: [40, 40, 46, 46],
                  position: "relative",
                  "& > :nth-child(1n)": {
                    position: "absolute",
                    height: ["1.5px", "3px", "3px", "3px"],
                    width: "100%",
                    background: crossBg,
                  },
                }}
              >
                <MotionBox
                  animate={{
                    rotate: "45deg",
                    top: "50%",
                    translateY: "-50%",
                    transition: {
                      ease: "easeInOut",
                      duration: 0.2,
                    },
                  }}
                />
                <MotionBox
                  animate={{
                    rotate: "-45deg",
                    top: "50%",
                    translateY: "-50%",
                    transition: {
                      ease: "easeInOut",
                      duration: 0.2,
                    },
                  }}
                />
              </Box>
            </MotionBox>
          </Box>
        </Fade>
      )}
    </AnimatePresence>,
    portalRoot,
  );
};

export default React.memo(Popup);
